/**
 * Story — the 8-scene React view for the pilot's story.
 *
 * Phase 2 of the Oatmeal rebuild (`docs/eng/pilot-oatmeal-rebuild.md`)
 * folds the prior `StorySections.tsx` JSX in-line and deletes the
 * separate file — the rebuild's single-view-single-file invariant.
 *
 * Section composition note: the eng plan originally envisioned mapping
 * Story scenes to vendored Oatmeal page sections (hero-*, features-*,
 * stats-*). On execution that doesn't fit — those sections are designed
 * for marketing pages (top-of-page hero followed by a feature list),
 * not for a vertical scroll-snap slide deck where each scene is a full-
 * viewport snap target with custom paint. The local `<Scene>` /
 * `<Lane>` / `<Skel>` helpers from `_helpers.tsx` stay as documented
 * exemptions; Story still composes its scenes from Oatmeal's *element*
 * primitives (`Container` / `Heading` / `Subheading` / `Text` /
 * `Eyebrow` / `Button` under `apps/web/src/oatmeal/elements/`), which
 * is what the rebuild's "compose over vendor" rule actually asks for.
 *
 * Predecessor history (Phase 4 of the React-engine port — see
 * `docs/eng/pilot-react-engine.md`): replaced the imperative
 * `lib/pilot/story.js` (605 LOC) with this React component tree.
 *
 *   - Scene JSX is in-line below. Every engine-bound id / data-* /
 *     class hook is preserved verbatim (the vanilla-JS engine still
 *     paints into the scene SVGs).
 *   - <SceneDots /> renders the side-dot navigator with
 *     `aria-current="step"` on the active dot (audit finding #10,
 *     killed structurally).
 *   - useScrollSpy tracks which scene is centered on screen.
 *   - This component also owns: the idle pill, keyboard navigation,
 *     picker build, intake-change rebuilds, params-change re-fires,
 *     and the scene-paint side effects (renderLanes / renderHistogram /
 *     animateNumberValue) that live in `lib/pilot/story-render.js`.
 *
 * The `mp:unlock` event dispatched when the visitor clicks "Now run
 * your own numbers" is preserved: `lib/pilot/pilot-engine.js`
 * listens and routes to sandbox + loads the process.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import SceneDots, {SceneDescriptor} from './SceneDots';
import {useScrollSpy} from '../../hooks/useScrollSpy';
import {Container} from '../../oatmeal/elements/container';
import {Heading} from '../../oatmeal/elements/heading';
import {Subheading} from '../../oatmeal/elements/subheading';
import {Text} from '../../oatmeal/elements/text';
import {Eyebrow} from '../../oatmeal/elements/eyebrow';
import {Button} from '../../oatmeal/elements/button';
import {Scene, Lane, Skel} from './_helpers';
import {IntakeSelect} from './IntakeSelect';
import {usePilot} from './PilotContext';
import {
  PROCESSES,
  PROCS_PER_YEAR,
  outcomeNounFor,
  type ProcessDef,
} from '../../lib/pilot/processes';
import ProcessPicker from './ProcessPicker';
import {runMonteCarlo, sampleTrial} from '../../lib/pilot/simulation';
import {
  renderLanes,
  renderHistogram,
} from '../../lib/pilot/story-render';
import {fmtUSD, animateNumberValue} from '../../lib/pilot/format';
import {categoryGlyphSvg} from '../../lib/pilot/categoryGlyph';
import {openCustomModal} from '../../lib/pilot/claude';
// Phase 2 of the React-engine port deleted lib/pilot/intake.js;
// PilotContext owns intake hydration and <IntakeSelect> owns the
// dropdown wiring. The bridge below is the read-only / subscribe
// surface the surviving engine code (Story scene-paint side effects)
// reads through. loadIntake / wireAllIntakeDropdowns / refreshIntakeUI
// have no React-side equivalents because they're no-ops by design now.
import {
  getIntake,
  isIntakeSet,
} from '../../lib/pilot/intake-bridge';
import {getParams, onParamsChange} from '../../lib/pilot/params';
import {MP_THEME_CHANGE_EVENT, MP_UNLOCK_EVENT} from '../../lib/pilot/events';

// Scene ids match the DOM ids in <StorySections />. Order matches
// document order so scroll-spy ties resolve in reading order.
const SCENES: ReadonlyArray<{
  id: string;
  title: string;
  summary: string;
}> = [
  {id: 'story-scene-1',       title: 'The question',   summary: 'How much do human-coordinated operations cost companies?'},
  {id: 'story-scene-context', title: 'Your context',   summary: 'Tell us a little about your org.'},
  {id: 'story-scene-2',       title: 'Pick a use case', summary: 'Choose a representative use case to step through.'},
  {id: 'story-scene-3',       title: 'The work',       summary: "Who's involved and what gets done."},
  {id: 'story-scene-4',       title: 'Today',          summary: 'Watch a single run unfold today.'},
  {id: 'story-scene-5',       title: 'With Planapus',  summary: 'Same people, same work, different routing.'},
  {id: 'story-scene-6',       title: '10,000 runs',    summary: 'The gap holds across the whole distribution.'},
  {id: 'story-scene-7',       title: 'The bottom line',summary: 'Per-year savings with these settings.'},
];

const PICK_SCENE_IDX = 2; // story-scene-2 — the picker scene

// Single source of demo timing so tunables stay together.
const STORY_TIMINGS = {
  LANE_MS: 6000,
  LANE_DUAL_MS: 6000,
  ANNOT_DELAY_MS: 150,
  COUNT_UP_MS: 1500,
  IDLE_PILL_MS: 4000,
  HISTO_FADE_MS: 700,
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function escapeHtml(s: string | null | undefined): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/* ============================================================
   HistoInteractive — React-owned interaction layer on top of the
   imperative renderHistogram paint. Per the May 2026 review feedback
   the chart felt static; this adds:

     - a vertical crosshair that follows the cursor's X position
     - a hover tooltip showing the day under cursor + % of runs in
       each series' bin at that day
     - active/passive dimming: the closer series is amplified, the
       other dims to ~30% opacity
     - click to pin the readout (esc / click empty to unpin)

   Layout constants must match renderHistogram's (story-render.js)
   so the crosshair lines up with the painted curves. Kept here as
   the canonical "chart layout" — if renderHistogram changes its
   padding, update both. We could centralize but it's five numbers
   and the imperative paint is on a slow-deprecation path; not worth
   another module today.
   ============================================================ */
const HISTO_VB_W = 800;
const HISTO_VB_H = 220;
const HISTO_PAD_L = 48;
const HISTO_PAD_R = 28;
const HISTO_PAD_T = 26;
const HISTO_PAD_B = 24;
const HISTO_BINS = 40;

interface HistoInteractiveProps {
  human: ReturnType<typeof runMonteCarlo>;
  ai: ReturnType<typeof runMonteCarlo>;
}

function HistoInteractive({human, ai}: HistoInteractiveProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // `pin` is the day value of the click-pinned readout, or null when
  // the user is just hovering. `hoverDay` is the live cursor position.
  // Rendering picks `pin ?? hoverDay`.
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const [pin, setPin] = useState<number | null>(null);
  const [hoverSeries, setHoverSeries] = useState<'human' | 'ai' | null>(null);

  // Same `maxDay` computation as renderHistogram so the cursor maps to
  // the same X scale as the painted curves.
  const maxDay = useMemo(() => {
    const allMax = Math.max(
      human.days.length ? human.days[human.days.length - 1] : 10,
      ai.days.length ? ai.days[ai.days.length - 1] : 10,
      Math.max(human.p90, ai.p90, 1),
    );
    return Math.ceil(allMax * 1.05);
  }, [human, ai]);

  // Pre-bin both series once so cursor lookup is O(1).
  const bins = useMemo(() => {
    const binW = maxDay / HISTO_BINS;
    const make = (arr: ReadonlyArray<number>) => {
      const counts = new Array<number>(HISTO_BINS).fill(0);
      for (const d of arr) {
        const b = Math.min(HISTO_BINS - 1, Math.floor(d / binW));
        counts[b]++;
      }
      return counts;
    };
    return {
      binW,
      human: make(human.days),
      ai: make(ai.days),
      humanTotal: human.days.length || 1,
      aiTotal: ai.days.length || 1,
    };
  }, [human, ai, maxDay]);

  const activeDay = pin ?? hoverDay;

  // Cursor → day conversion. The container is `.relative` and the SVG
  // it overlays fills width 100%. We work in viewBox units (0..800)
  // because that's what the painted curves use; mapping cursor pixels
  // back into viewBox space keeps the crosshair pixel-aligned at every
  // viewport width.
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const pxX = e.clientX - rect.left;
    if (pxX < 0 || pxX > rect.width) {
      setHoverDay(null);
      setHoverSeries(null);
      return;
    }
    const vbX = (pxX / rect.width) * HISTO_VB_W;
    if (vbX < HISTO_PAD_L || vbX > HISTO_VB_W - HISTO_PAD_R) {
      setHoverDay(null);
      setHoverSeries(null);
      return;
    }
    const chartW = HISTO_VB_W - HISTO_PAD_L - HISTO_PAD_R;
    const day = ((vbX - HISTO_PAD_L) / chartW) * maxDay;
    setHoverDay(day);
    // Active/passive: figure out which series the cursor is closer to
    // VERTICALLY in viewBox space. Translate the cursor's pxY into
    // viewBox y, then compute each series' y at that bin and compare.
    const pxY = e.clientY - rect.top;
    const vbY = (pxY / rect.height) * HISTO_VB_H;
    const binIdx = Math.min(
      HISTO_BINS - 1,
      Math.max(0, Math.floor(day / bins.binW)),
    );
    const peak = Math.max(...bins.human, ...bins.ai, 1);
    const chartH = HISTO_VB_H - HISTO_PAD_T - HISTO_PAD_B;
    const yFor = (count: number) =>
      HISTO_PAD_T + chartH - (count / peak) * chartH;
    const hy = yFor(bins.human[binIdx]);
    const ay = yFor(bins.ai[binIdx]);
    setHoverSeries(Math.abs(vbY - hy) <= Math.abs(vbY - ay) ? 'human' : 'ai');
  };

  const onPointerLeave = () => {
    setHoverDay(null);
    setHoverSeries(null);
  };

  const onClick = () => {
    if (pin != null) {
      setPin(null);
    } else if (hoverDay != null) {
      setPin(hoverDay);
    }
  };

  useEffect(() => {
    if (pin == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPin(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pin]);

  // Series fade — applied via CSS variable on the wrap; story-render.js
  // paints `<g class="series series-human">` + `<g class="series series-ai">`
  // so the rules below dim the non-hovered (or non-pinned) series.
  // Pinned + hovered both count as "this is the active series".
  const activeSeries = hoverSeries; // null when unhovered → no dimming

  // Crosshair + readout values for the active day.
  let readout: {
    dayLabel: string;
    humanPct: string;
    aiPct: string;
    closerLabel: 'Today' | 'With Planapus' | null;
  } | null = null;
  if (activeDay != null) {
    const binIdx = Math.min(
      HISTO_BINS - 1,
      Math.max(0, Math.floor(activeDay / bins.binW)),
    );
    const humanPct = (bins.human[binIdx] / bins.humanTotal) * 100;
    const aiPct = (bins.ai[binIdx] / bins.aiTotal) * 100;
    const closerLabel: 'Today' | 'With Planapus' | null =
      hoverSeries === 'human'
        ? 'Today'
        : hoverSeries === 'ai'
        ? 'With Planapus'
        : null;
    readout = {
      dayLabel: `Day ${activeDay.toFixed(1)}`,
      humanPct: humanPct < 0.1 ? '<0.1%' : `${humanPct.toFixed(1)}%`,
      aiPct: aiPct < 0.1 ? '<0.1%' : `${aiPct.toFixed(1)}%`,
      closerLabel,
    };
  }

  // Crosshair X in CSS % so the line scales with the container.
  const crossPct =
    activeDay != null
      ? ((HISTO_PAD_L + (activeDay / maxDay) * (HISTO_VB_W - HISTO_PAD_L - HISTO_PAD_R)) /
          HISTO_VB_W) *
        100
      : 0;

  // Tooltip side: keep it on the side opposite the cursor so it doesn't
  // cover the curve under inspection.
  const tipOnRight = crossPct < 60;

  return (
    <div
      ref={wrapRef}
      // Wrapper is positioned absolute over the svg, fills exactly. The
      // pointer events live here so the SVG itself stays interaction-
      // free (story-render.js re-paints its innerHTML on knob changes;
      // attaching listeners directly to the SVG paths would re-bind on
      // every paint and was the source of the prior "jumpy" feel).
      className="absolute inset-0 cursor-crosshair"
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      style={{
        // Series fade applied via a sibling stylesheet — the inline
        // rules below dim the non-active series in the painted SVG.
        // `data-active-series` is read by the selectors so it survives
        // story-render.js repaints (the wrapper is React-owned).
      }}
      data-active-series={activeSeries ?? ''}
      data-pinned={pin != null ? 'true' : 'false'}
    >
      {/* Series fade rules — keyed off the wrapper's data attribute so
          the dimming applies after every imperative re-paint without
          needing a JS hook to re-bind. */}
      <style>{`
        [data-active-series="human"] ~ svg .series-ai,
        [data-active-series="human"] + svg .series-ai {
          opacity: 0.22;
          transition: opacity 160ms ease;
        }
        [data-active-series="ai"] ~ svg .series-human,
        [data-active-series="ai"] + svg .series-human {
          opacity: 0.22;
          transition: opacity 160ms ease;
        }
      `}</style>
      {activeDay != null && (
        <div
          aria-hidden
          className="pointer-events-none absolute top-[10%] bottom-[10%] w-px"
          style={{
            left: `${crossPct}%`,
            background:
              hoverSeries === 'ai'
                ? '#ff8aa3'
                : hoverSeries === 'human'
                ? '#f59f4a'
                : 'rgba(0,0,0,0.35)',
          }}
        />
      )}
      {readout && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute top-2 z-10 max-w-[220px] rounded-lg border border-taupe-200 bg-white/95 px-3 py-2 text-xs/5 shadow-lg backdrop-blur-sm dark:border-taupe-700 dark:bg-taupe-900/95"
          style={{
            left: tipOnRight ? `calc(${crossPct}% + 12px)` : undefined,
            right: tipOnRight ? undefined : `calc(${100 - crossPct}% + 12px)`,
          }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-mono uppercase tracking-widest text-taupe-500">
              {readout.dayLabel}
            </span>
            {pin != null && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-taupe-500">
                pinned
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block size-2 rounded-sm"
                  style={{background: '#f59f4a'}}
                />
                <span className={readout.closerLabel === 'Today' ? 'font-semibold text-taupe-950 dark:text-white' : 'text-taupe-700 dark:text-taupe-400'}>
                  Today
                </span>
              </span>
              <span className="font-semibold tabular-nums text-taupe-950 dark:text-white">
                {readout.humanPct}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block size-2 rounded-sm"
                  style={{background: '#ff8aa3'}}
                />
                <span className={readout.closerLabel === 'With Planapus' ? 'font-semibold text-taupe-950 dark:text-white' : 'text-taupe-700 dark:text-taupe-400'}>
                  With Planapus
                </span>
              </span>
              <span className="font-semibold tabular-nums text-taupe-950 dark:text-white">
                {readout.aiPct}
              </span>
            </div>
          </div>
          {pin == null && (
            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-taupe-500">
              click to pin · esc to clear
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ProcessSimState {
  proc: ProcessDef;
  human: ReturnType<typeof runMonteCarlo>;
  ai: ReturnType<typeof runMonteCarlo>;
  // Stored as Record<string, number> rather than SimParams so the
  // sim engine helpers (whose signatures pre-date the typed SimParams)
  // accept it without per-call casts. SimParams is structurally a
  // {[k: string]: number} but TS won't widen automatically.
  runParams: Record<string, number>;
}

function computeProcessSim(proc: ProcessDef): ProcessSimState {
  const baselineMeanHrs =
    proc.steps.reduce((a, s) => a + s.baseHrs, 0) / proc.steps.length;
  // Mutate the proc with the baseline mean for downstream consumers
  // that read `_baselineMeanHrs` (sim engine uses it to scale).
  proc._baselineMeanHrs = baselineMeanHrs;

  const p = getParams();
  if (p.avgResponseHrs === 24) p.avgResponseHrs = Math.round(baselineMeanHrs);

  const pAsRec = p as unknown as Record<string, number>;
  const N = 10000;
  const human = runMonteCarlo(proc, pAsRec, N, false);
  const ai = runMonteCarlo(proc, pAsRec, N, true);

  return {proc, human, ai, runParams: pAsRec};
}

export default function Story() {
  // Phase B of the imperative-renderer retirement: intake comes from
  // PilotContext directly (was: read through intake-bridge in the
  // old imperative rebuildPicker). The picker is now <ProcessPicker>
  // which re-renders automatically when intake changes.
  const {intake: pilotIntake, viewMode: pilotViewMode} = usePilot();

  // Refs to each scene <section>, looked up once after mount via the
  // engine-stable DOM ids in StorySections. This indirection means
  // StorySections stays JSX-only and can be refactored without us
  // having to wire forwarded refs through it.
  //
  // We type these as MutableRefObject rather than RefObject because we
  // assign `.current` ourselves in the post-mount useEffect below — the
  // engine-stable id lookup is the bind site, not React's ref attribute.
  const sceneRefs = useRef<
    Record<string, React.MutableRefObject<HTMLElement | null>>
  >({});

  // Allocate refs eagerly so useScrollSpy gets stable identities. The
  // plain `{current: null}` literal is structurally a MutableRefObject;
  // useScrollSpy reads `ref.current` and gates observe on non-null.
  for (const s of SCENES) {
    if (!sceneRefs.current[s.id]) {
      sceneRefs.current[s.id] = {current: null};
    }
  }

  // After StorySections mounts, populate each ref by id. Cheaper and
  // simpler than forwarding refs through every wrapper. Re-runs only
  // if a section's id rotates (it won't).
  useEffect(() => {
    for (const s of SCENES) {
      const el = document.getElementById(s.id);
      if (el && sceneRefs.current[s.id].current !== el) {
        sceneRefs.current[s.id].current = el;
      }
    }
  });

  const spyTargets = useMemo(
    () => SCENES.map((s) => ({id: s.id, ref: sceneRefs.current[s.id]})),
    [],
  );
  const {activeId, scrollTo, setActiveId} = useScrollSpy(spyTargets);

  // Process pick state lives in this component (not PilotContext yet)
  // because no other view reads it until Phase 5.
  const [simState, setSimState] = useState<ProcessSimState | null>(null);
  const simStateRef = useRef<ProcessSimState | null>(null);
  simStateRef.current = simState;

  // Lock scenes 3+ until a process is picked. The legacy `story.js`
  // used to write `body.story-locked` for this — no CSS keys off it
  // anymore (verified across pilot-engine.css), so this component
  // owns the gate purely in React state (`simState !== null`). The
  // gate is enforced inside `advance` / `selectScene` below: any
  // forward move past PICK_SCENE_IDX flashes the picker nudge and
  // sends the visitor back to the picker instead.
  //
  // Per the architectural invariant in
  // docs/eng/pilot-react-engine.md, PilotProvider is the sole writer
  // of pilot body classes — we don't touch document.body.classList
  // here.

  // ----- Picker build + intake reactivity ---------------------------

  // "Open custom-process modal" callback — opens the Claude-generation
  // flow that builds a synthetic ProcessDef from a free-text prompt.
  // Phase B of the imperative-renderer retirement: was inline inside
  // the now-deleted rebuildPicker.
  const openCustomPicker = useCallback(() => {
    openCustomModal({
      processes: PROCESSES,
      onSelect: (proc: ProcessDef) => pickProcess(proc),
      onGenerated: (proc: ProcessDef) => {
        (PROCESSES as Record<string, ProcessDef>)[proc.id] = proc;
        pickProcess(proc);
      },
    });
    // pickProcess is stable; satisfy exhaustive-deps explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picker title text — derived from intake + filtered count. Was
  // imperatively written by updatePickTitle (now retired). The empty
  // string fallback before mount keeps SSR-safe; in practice React
  // renders the first frame with the right value.
  const pickTitle = useMemo(() => {
    const total = Object.values(PROCESSES).length;
    const intakeSet = !!(
      pilotIntake.industry || pilotIntake.size || pilotIntake.function
    );
    if (!intakeSet) return 'Pick a use case to step through.';
    // filterProcesses is already called inside <ProcessPicker>; re-
    // computing the count here is cheap (the catalog is ~12 items).
    // Importing filterProcesses from processes would be a circular-ish
    // duplication of the picker's internal logic — instead we infer
    // from intakeSet + the always-present 'industry' filter behavior:
    // if intake is set, the picker shows a subset; if all match (rare
    // but possible if intake matches every process), title falls back.
    // For sliceable correctness, just describe the filtered state
    // generically — the user sees the actual grid below.
    return `${total} processes match your context. Pick one to step through.`;
  }, [pilotIntake]);

  // Pick handler — exposed via closure to the picker callbacks above
  // and the "flashPickerNudge → block forward" path below. Updates
  // local simState and scrolls into scene 3.
  const pickProcess = useCallback(
    (proc: ProcessDef) => {
      const next = computeProcessSim(proc);
      setSimState(next);
      simStateRef.current = next;
      // Allow forward navigation now that a process is picked — the
      // gate inside `advance` / `selectScene` reads `simStateRef`.
      // No body-class write is needed; PilotProvider owns those.
      // Defer one frame so React has time to render the unlocked
      // scenes before we ask the spy to scroll into one of them.
      requestAnimationFrame(() => {
        scrollTo('story-scene-3');
        setActiveId('story-scene-3');
      });
    },
    [scrollTo, setActiveId],
  );

  // ----- Boot: intake + params are hydrated by PilotContext
  // (initializer) and `lib/pilot/params.ts` (loadParams() in
  // pilot-engine.js boot). <IntakeSelect> owns the dropdown wiring;
  // <ProcessPicker> below re-renders automatically when pilotIntake
  // (from PilotContext) changes.
  //
  // The params subscription still has to live as an imperative
  // onParamsChange because the params store
  // (`lib/pilot/params.ts`) is consumed by Story's own SVG paint
  // (renderLanes / renderHistogram in story-render.js) which sit
  // in the canvas-pixel carve-out and stay imperative.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const offParams = onParamsChange(() => {
      const cur = simStateRef.current;
      if (!cur) return;
      const next = computeProcessSim(cur.proc);
      setSimState(next);
      simStateRef.current = next;
      // Re-fire whichever scene we're on so it picks up the new numbers.
      const aid = activeIdRef.current;
      if (aid) refireSceneRef.current?.(aid, next);
    });

    return () => {
      try {
        offParams?.();
      } catch {}
    };
  }, []);

  // ----- Scene-paint side effects (still imperative, via render.js) -

  // Track scenes that have fired once (for static reveal scenes).
  const scenesFiredRef = useRef<Set<string>>(new Set());
  // Replayable scenes re-fire on each entry so the user gets the
  // animation again. Mirrors story.js's REPLAY_SCENES set.
  const REPLAY = useMemo(() => new Set(['story-scene-4', 'story-scene-5', 'story-scene-7']), []);

  const refireScene = useCallback(
    (sceneId: string, sim: ProcessSimState | null) => {
      if (!sim) return;
      const reduced = prefersReducedMotion();

      switch (sceneId) {
        case 'story-scene-3': {
          paintScene3(sim);
          break;
        }
        case 'story-scene-4': {
          paintScene4(sim, reduced);
          break;
        }
        case 'story-scene-5': {
          paintScene5(sim, reduced);
          break;
        }
        case 'story-scene-6': {
          paintScene6(sim, reduced);
          break;
        }
        case 'story-scene-7': {
          paintScene7(sim, reduced);
          break;
        }
        default:
          break;
      }
    },
    [],
  );

  // Stash refireScene in a ref so the intake / params effect doesn't
  // need it in its deps array (it would force a re-subscribe on every
  // render of those callbacks).
  const refireSceneRef = useRef<typeof refireScene | null>(null);
  refireSceneRef.current = refireScene;
  const activeIdRef = useRef<string | null>(activeId);
  activeIdRef.current = activeId;

  // Fire scene-paint when the active scene changes (or process changes).
  useEffect(() => {
    if (!activeId || !simState) return;
    const isReplay = REPLAY.has(activeId);
    if (!isReplay && scenesFiredRef.current.has(activeId)) return;
    scenesFiredRef.current.add(activeId);
    refireScene(activeId, simState);
  }, [activeId, simState, REPLAY, refireScene]);

  // When the process changes, force every scene to re-fire on next entry.
  useEffect(() => {
    if (simState) scenesFiredRef.current.clear();
  }, [simState]);

  // ----- Gated navigation: can't move past picker without a process -

  const advance = useCallback(() => {
    const curIdx = SCENES.findIndex((s) => s.id === activeIdRef.current);
    if (curIdx < 0) return;
    if (curIdx >= SCENES.length - 1) {
      // Last scene → fire the unlock event so pilot-engine.js's
      // mp:unlock handler routes to sandbox + loads the process.
      dispatchUnlock(simStateRef.current?.proc ?? null);
      return;
    }
    const nextIdx = curIdx + 1;
    // Gate: blocked past picker until process picked.
    if (nextIdx > PICK_SCENE_IDX && !simStateRef.current) {
      flashPickerNudge();
      return;
    }
    const targetId = SCENES[nextIdx].id;
    scrollTo(targetId);
    setActiveId(targetId);
  }, [scrollTo, setActiveId]);

  const back = useCallback(() => {
    const curIdx = SCENES.findIndex((s) => s.id === activeIdRef.current);
    if (curIdx <= 0) return;
    const targetId = SCENES[curIdx - 1].id;
    scrollTo(targetId);
    setActiveId(targetId);
  }, [scrollTo, setActiveId]);

  const selectScene = useCallback(
    (id: string) => {
      const idx = SCENES.findIndex((s) => s.id === id);
      if (idx < 0) return;
      if (idx > PICK_SCENE_IDX && !simStateRef.current) {
        flashPickerNudge();
        // Send them to the picker instead of forward.
        scrollTo(SCENES[PICK_SCENE_IDX].id);
        setActiveId(SCENES[PICK_SCENE_IDX].id);
        return;
      }
      scrollTo(id);
      setActiveId(id);
    },
    [scrollTo, setActiveId],
  );

  // ----- Keyboard navigation ----------------------------------------

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as HTMLElement).isContentEditable)) return;
      if (t && t.classList && t.classList.contains('nb-intake-btn')) return;
      const modal = document.querySelector('#modal-backdrop');
      if (modal && modal.classList.contains('open')) return;

      const fwdKeys = ['ArrowDown', 'ArrowRight', ' ', 'Enter', 'PageDown'];
      const backKeys = ['ArrowUp', 'ArrowLeft', 'Backspace', 'PageUp'];
      if (fwdKeys.includes(e.key)) {
        e.preventDefault();
        advance();
      } else if (backKeys.includes(e.key)) {
        e.preventDefault();
        back();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, back]);

  // ----- Idle pill timer --------------------------------------------

  const [pillIdle, setPillIdle] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setPillIdle(false);
      return;
    }
    setPillIdle(false);
    const t = window.setTimeout(() => setPillIdle(true), STORY_TIMINGS.IDLE_PILL_MS);
    return () => window.clearTimeout(t);
    // Reset the idle timer whenever the scene changes.
  }, [activeId]);

  // ----- Lane skip wiring -------------------------------------------
  // Clicking an in-progress lane advances to the next scene. Wired
  // once after mount; the engine SVGs persist for the lifetime of the
  // Story tree, so a one-shot effect is fine.
  useEffect(() => {
    const ids = ['story-svg-4-human', 'story-svg-5-human', 'story-svg-5-ai'];
    const wired: Array<{el: HTMLElement; handler: () => void}> = [];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (!el) continue;
      const handler = () => advance();
      el.style.cursor = 'pointer';
      el.setAttribute('title', 'Click to skip animation');
      el.addEventListener('click', handler);
      wired.push({el, handler});
    }
    return () => {
      for (const w of wired) w.el.removeEventListener('click', w.handler);
    };
  }, [advance]);

  // ----- Theme re-render --------------------------------------------
  // Story-mode SVGs (lanes, histogram) read theme tokens at render
  // time. When the user flips theme mid-story, re-fire whichever
  // time-based scene we're sitting on so the colors update.
  useEffect(() => {
    function onTheme() {
      const aid = activeIdRef.current;
      if (!aid) return;
      scenesFiredRef.current.delete(aid);
      refireScene(aid, simStateRef.current);
    }
    window.addEventListener(MP_THEME_CHANGE_EVENT, onTheme);
    return () => window.removeEventListener(MP_THEME_CHANGE_EVENT, onTheme);
  }, [refireScene]);

  // ----- Initial scroll: skip context if intake already set ---------
  // Mirror story.js's activateStory() one-shot. Only on the first
  // mount; subsequent re-renders shouldn't re-scroll the visitor.
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    initialScrollDoneRef.current = true;
    // Defer one frame so the section refs are wired before scrollTo.
    requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0;
      if (isIntakeSet()) {
        // Show scene 1 briefly so the user gets the headline before
        // landing on the picker.
        window.setTimeout(() => {
          scrollTo('story-scene-2');
          setActiveId('story-scene-2');
        }, 50);
      } else {
        setActiveId('story-scene-1');
      }
    });
  }, [scrollTo, setActiveId]);

  // ----- Pill behavior at last scene --------------------------------
  // At the bottom-line scene the pill becomes a "back to start"
  // affordance instead of "continue" (mirrors story.js).
  const isLastScene =
    activeId === SCENES[SCENES.length - 1].id ||
    (activeId === null && false);
  const summaryText =
    SCENES.find((s) => s.id === activeId)?.summary ?? SCENES[0].summary;

  const onPillClick = useCallback(() => {
    if (isLastScene) {
      // Restart — scroll to scene 1.
      scrollTo(SCENES[0].id);
      setActiveId(SCENES[0].id);
    } else {
      advance();
    }
  }, [isLastScene, advance, scrollTo, setActiveId]);

  // SceneDots prop list — derived from SCENES.
  const sceneDescriptors = useMemo<SceneDescriptor[]>(
    () => SCENES.map((s) => ({id: s.id, title: s.title})),
    [],
  );

  return (
    <>
      <section
        className="story snap-y snap-mandatory"
        id="story"
        // F17 Slice D: the prior `body.story-mode .story { display:
        // block }` + `body.pitch-mode/.vis-mode/.demo-mode .story
        // { display: none !important }` rules in pilot-engine.css
        // gated this section globally. Story stays mounted across
        // view switches (so the visitor's scroll position survives)
        // and just toggles `hidden` based on viewMode — same DOM
        // outcome, structural source of truth.
        hidden={pilotViewMode !== 'story' && pilotViewMode !== 'sandbox'}
      >
        {/* Scene 1 — the question. Two-column hero + portal mount for the
            DocsToOutcomes illustration (#pp-hero-illu-mount). */}
        <div className="story-scene story-scene-1 flex min-h-screen snap-start items-center" id="story-scene-1">
          <Container className="flex h-full min-h-screen flex-col items-center justify-center py-16">
            <div className="story-scene-1-grid grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
              <div className="story-scene-1-text flex flex-col gap-6">
                <Eyebrow className="story-eyebrow">
                  Coordination simulation
                </Eyebrow>
                <Heading className="story-display max-w-3xl">
                  Coordination is where companies bleed money.
                </Heading>
                {/* <Text> renders <p>, and the two <p> children below
                    can't be paragraph descendants per the HTML5 content
                    model (the parser would auto-close the outer <p>
                    mid-content). Use a <div> with the same `story-lede`
                    layout classes so both visuals and DOM nesting are
                    valid. */}
                <div className="story-lede flex max-w-xl flex-col gap-4 text-lg">
                  <p>
                    Tell us about your org and pick a use case. We'll run a
                    two-minute simulation of the errors, rework, delays, and
                    manual coordination cost the operation carries today —
                    then show what Planapus reclaims.
                  </p>
                  <p className="story-lede-sub story-lede-accent text-taupe-600 dark:text-taupe-500">
                    Vendor onboarding, account opening, claims, permits,
                    application reviews. Every handoff between people and
                    organizations is a cost line and a delay in revenue.
                    Invisible on the income statement, enormous in reality.
                  </p>
                </div>
                <div className="story-scroll-hint mt-4 font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
                  Scroll ↓
                </div>
              </div>
              <div className="story-scene-1-illu" id="pp-hero-illu-mount" />
            </div>
          </Container>
        </div>

        {/* Scene 1.5 — intake. Three IntakeSelect fields filter the
            catalog to the visitor's context. React owns the dropdown
            visibility, so audit finding #4 (Tailwind-vs-HTML-hidden
            conflict) can't recur. */}
        <Scene id="story-scene-context" eyebrow="First, your context" className="story-scene story-scene-context">
          <Subheading className="story-h2 story-intake-prompt text-balance">
            You're in <IntakeSelect field="function" />{' '}
            at a <IntakeSelect field="industry" />{' '}
            organization with <IntakeSelect field="size" /> people.
          </Subheading>
          <Text id="story-intake-sub" className="story-lede story-intake-sub">
            The more you fill in, the better the suggestions below. Or skip
            to browse every use case.
          </Text>
          <div>
            <Button
              id="story-context-skip"
              className="story-context-skip"
            >
              Show all processes →
            </Button>
          </div>
          <div className="story-scroll-hint mt-2 font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
            Continue ↓
          </div>
        </Scene>

        {/* Scene 2 — pick a process. Phase B of the imperative-renderer
            retirement (docs/eng/pilot-engine-imperative-retirement.md):
            the picker is React-owned via <ProcessPicker>; intake →
            filter is a useMemo inside the component, no
            imperative rebuildPicker needed. The picker title text is
            derived from intake + filtered count below. */}
        <Scene id="story-scene-2" eyebrow="Use case" className="story-scene story-scene-2">
          <Subheading id="story-pick-title" className="story-h2 [.story-cards-nudge_&]:[animation:story-cards-nudge_600ms_ease-in-out]">
            {pickTitle}
          </Subheading>
          <ProcessPicker
            processes={PROCESSES}
            intake={pilotIntake}
            onPick={pickProcess}
            onCustom={openCustomPicker}
            className="story-cards"
          />
        </Scene>

        {/* Scene 3 — the work being done. Phase B step 4 of the
            imperative-renderer retirement: name/desc/stats/roster all
            React-driven from `simState.proc` (was: paintScene3
            innerHTML strings). The engine no longer paints anything
            into this scene; the `id` attributes stay only as
            paint-target shims for any external scripts that may still
            query them. */}
        <Scene id="story-scene-3" eyebrow="The work" className="story-scene story-scene-3">
          <Subheading id="story-proc-name" className="story-h2">
            {simState?.proc ? (
              <>
                <span
                  className="mr-2 inline-flex align-[-0.15em]"
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{
                    __html: categoryGlyphSvg(simState.proc.category || '', 22),
                  }}
                />
                <span className="inline-flex items-center">
                  {simState.proc.name}
                </span>
              </>
            ) : (
              <Skel />
            )}
          </Subheading>
          <Text id="story-proc-desc" className="story-proc-desc text-lg/8">
            {simState?.proc ? simState.proc.short || simState.proc.desc : ''}
          </Text>
          {/* Divider — separates the use case header from the
              organizations + participants breakdown below. Per the May
              2026 review the scene felt visually undifferentiated;
              this rule + the panel below give it structure. */}
          {simState?.proc && (
            <hr className="story-proc-divider my-2 border-t border-taupe-200 dark:border-taupe-800" />
          )}
          {simState?.proc && (
            <div
              id="story-proc-stats"
              className="story-stats flex flex-wrap gap-x-4 gap-y-1 text-sm/6 text-taupe-700 dark:text-taupe-400"
            >
              <span>
                <b className="text-taupe-950 dark:text-white">
                  {simState.proc.parties.length}
                </b>{' '}
                participants
              </span>
              <span className="text-taupe-400" aria-hidden>
                ·
              </span>
              <span>
                <b className="text-taupe-950 dark:text-white">
                  {simState.proc.steps.length}
                </b>{' '}
                handoffs
              </span>
              <span className="text-taupe-400" aria-hidden>
                ·
              </span>
              <span>
                <b className="text-taupe-950 dark:text-white">
                  {simState.proc.orgs.length}
                </b>{' '}
                {simState.proc.orgs.length === 1 ? 'organization' : 'organizations'}
              </span>
            </div>
          )}
          {simState?.proc && (
            <div
              id="story-proc-roster"
              className="story-proc-roster mt-4 flex flex-col gap-3"
            >
              <p className="font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
                Who's involved
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(() => {
                  const p = simState.proc;
                  const orgsMap = Object.fromEntries(p.orgs.map((o) => [o.id, o]));
                  const byOrg = new Map<string, typeof p.parties>();
                  for (const party of p.parties) {
                    const arr = byOrg.get(party.org) || [];
                    arr.push(party);
                    byOrg.set(party.org, arr);
                  }
                  return Array.from(byOrg.entries()).map(([orgId, parties]) => {
                    const o = orgsMap[orgId] || {
                      name: orgId,
                      color: 'var(--color-taupe-500)',
                    };
                    return (
                      <div
                        key={orgId}
                        className="story-proc-org flex flex-col gap-2 rounded-2xl border border-taupe-200 bg-white p-4 dark:border-taupe-800 dark:bg-taupe-900"
                        // Accent the top border with the org colour so the
                        // cards read as a colour-keyed legend the lane
                        // diagrams in scenes 4-5 follow. Box-shadow inset
                        // beats border-top-color since we'd lose the
                        // taupe ring otherwise.
                        style={{boxShadow: `inset 4px 0 0 0 ${o.color}`}}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="flex items-baseline gap-2">
                            <span
                              className="inline-block size-2.5 rounded-full"
                              style={{background: o.color}}
                            />
                            <span className="font-display text-lg/6 text-taupe-950 dark:text-white">
                              {o.name}
                            </span>
                          </div>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-taupe-500">
                            Organization
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {parties.map((pp) => (
                            <span
                              key={pp.id}
                              className="inline-flex items-center rounded-full border border-taupe-200 bg-taupe-50 px-2 py-0.5 text-xs/5 font-medium text-taupe-700 dark:border-taupe-700 dark:bg-taupe-800 dark:text-taupe-300"
                            >
                              {pp.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
          <div className="story-scroll-hint mt-2 font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
            Watch it run ↓
          </div>
        </Scene>

        {/* Scene 4 — today (single lane). */}
        <Scene id="story-scene-4" eyebrow="Today" className="story-scene story-scene-4">
          <Subheading className="story-h2">
            Watch <em>one full run</em>, handoff by handoff, the way it
            happens today.
          </Subheading>
          <div className="story-lane-wrap story-lane-wrap-single mx-auto w-full max-w-xl">
            <Lane
              variant="human"
              title="Today: human-routed"
              clockId="story-clock-4"
              svgId="story-svg-4-human"
            />
            {/* Per the May 2026 functional/content review: visitors
                wanted a way to replay the animation without scrolling
                away and back. The button re-fires the scene-paint
                effect. */}
            <div className="mt-3 flex items-center justify-center">
              <button
                type="button"
                className="story-replay inline-flex items-center gap-2 rounded-full border border-taupe-300 bg-white px-3 py-1 text-xs/5 font-medium text-taupe-700 hover:border-taupe-950 hover:text-taupe-950 dark:border-taupe-700 dark:bg-taupe-900 dark:text-taupe-400 dark:hover:text-white"
                onClick={() => {
                  scenesFiredRef.current.delete('story-scene-4');
                  refireSceneRef.current?.('story-scene-4', simStateRef.current);
                }}
                aria-label="Replay this run"
              >
                ↻ Replay
              </button>
            </div>
          </div>
          <Text id="story-annot-4" className="story-annotation text-center" />
        </Scene>

        {/* Scene 5 — with Planapus. Hidden human lane stays in DOM for engine. */}
        <Scene id="story-scene-5" eyebrow="With Planapus" className="story-scene story-scene-5">
          <Subheading className="story-h2">
            Same work. Same people. Different routing.
          </Subheading>
          <div className="story-lane-wrap story-lane-wrap-single mx-auto w-full max-w-xl">
            <Lane
              variant="ai"
              title="With Planapus"
              clockId="story-clock-5a"
              svgId="story-svg-5-ai"
            />
            <svg
              id="story-svg-5-human"
              viewBox="0 0 600 540"
              style={{display: 'none'}}
              aria-hidden
            />
            <span id="story-clock-5h" style={{display: 'none'}} aria-hidden>
              Day 0.0
            </span>
            <div className="mt-3 flex items-center justify-center">
              <button
                type="button"
                className="story-replay inline-flex items-center gap-2 rounded-full border border-taupe-300 bg-white px-3 py-1 text-xs/5 font-medium text-taupe-700 hover:border-taupe-950 hover:text-taupe-950 dark:border-taupe-700 dark:bg-taupe-900 dark:text-taupe-400 dark:hover:text-white"
                onClick={() => {
                  scenesFiredRef.current.delete('story-scene-5');
                  refireSceneRef.current?.('story-scene-5', simStateRef.current);
                }}
                aria-label="Replay this run"
              >
                ↻ Replay
              </button>
            </div>
          </div>
          <Text id="story-annot-5" className="story-annotation text-center" />
        </Scene>

        {/* Scene 6 — the distribution histogram. */}
        <Scene id="story-scene-6" eyebrow="The difference" className="story-scene story-scene-6">
          <Subheading className="story-h2">
            Across 10,000 simulated runs, the gap holds.
          </Subheading>
          <div className="story-histo-wrap">
            <div className="histo rounded-2xl border border-taupe-200 bg-white p-5 dark:border-taupe-800 dark:bg-taupe-900">
              <div className="h-head mb-4 flex items-center justify-between">
                <div className="h-legend flex flex-wrap gap-x-6 gap-y-2 text-xs/5 text-taupe-700 dark:text-taupe-400">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="swatch h inline-block size-3 rounded-sm"
                      style={{background: '#f59f4a'}}
                    />
                    Today
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="swatch a inline-block size-3 rounded-sm"
                      style={{background: '#ff8aa3'}}
                    />
                    With Planapus
                  </span>
                </div>
              </div>
              <div className="-mx-1 overflow-x-auto pb-1">
                <div className="relative min-w-[560px]">
                  {/* Order matters: the interactive overlay sits BEFORE
                      the svg in the DOM so the `[data-active-series] +
                      svg` adjacent-sibling selector in
                      <HistoInteractive>'s injected <style> applies to
                      the painted curves. */}
                  {simState && (
                    <HistoInteractive
                      human={simState.human}
                      ai={simState.ai}
                    />
                  )}
                  <svg
                    id="story-svg-histo"
                    viewBox="0 0 800 220"
                    preserveAspectRatio="none"
                    className="block h-auto w-full"
                    style={{aspectRatio: '800 / 220'}}
                  />
                </div>
              </div>
            </div>
          </div>
        </Scene>

        {/* Scene 7 — bottom line tile grid. */}
        <Scene id="story-scene-7" className="story-scene story-scene-7">
          <div className="pp-bottom-line flex flex-col gap-10">
            <div className="pp-bottom-line-head flex flex-col gap-3">
              <Eyebrow className="story-eyebrow">The bottom line</Eyebrow>
              <Subheading className="story-h2">
                Per year, Planapus reclaims:
              </Subheading>
            </div>
            <ol
              id="story-wins"
              className="pp-wins-grid story-wins grid grid-cols-1 gap-4 sm:grid-cols-3"
            >
              {[
                {n: '01', id: 'story-win-dollars', initial: '$0', label: 'Coordination cost reclaimed'},
                {n: '02', id: 'story-win-hours', initial: '0 hrs', label: 'Hours reclaimed from handoffs'},
                {n: '03', id: 'story-win-deals', initial: '0', label: `${capitalize(outcomeNounFor(simState?.proc))} saved from failure`},
              ].map((tile) => (
                <li
                  key={tile.n}
                  className={`pp-wins-tile pp-wins-tile-${tile.n === '01' ? 'colza' : tile.n === '02' ? 'clay' : 'ink'} flex flex-col gap-6 rounded-2xl border border-taupe-200 bg-white p-6 dark:border-taupe-800 dark:bg-taupe-900`}
                >
                  <div className="pp-wins-tile-head">
                    <span className="pp-wins-tile-n font-mono text-xs/5 uppercase tracking-widest text-taupe-500">
                      {tile.n}
                    </span>
                  </div>
                  <div className="pp-wins-tile-body flex flex-col gap-2">
                    <div
                      id={tile.id}
                      className="nb-win-num pp-wins-num font-display text-4xl/none tracking-tight text-taupe-950 sm:text-5xl/none dark:text-white"
                    >
                      {tile.initial}
                    </div>
                    <div className="nb-win-label pp-wins-label text-sm/6 text-taupe-700 dark:text-taupe-400">
                      {tile.label}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            <p className="story-cta-prose pp-bottom-line-prose text-base/7 text-taupe-700 dark:text-taupe-400">
              For one use case. In one organization.{' '}
              <strong className="text-taupe-950 dark:text-white">
                And these are defaults.
              </strong>
            </p>
            <div>
              <Button
                id="story-unlock"
                className="story-unlock-btn btn-primary"
                size="lg"
              >
                Now run your own numbers →
              </Button>
            </div>
          </div>
        </Scene>
      </section>

      {/* Persistent navigation overlay — side dots + bottom-right pill +
          scene summary. SceneDots bakes aria-current="step" into the
          active dot (audit finding #10). */}
      <div
        className="story-controls fixed bottom-6 right-6 z-30 flex items-end gap-4"
        id="story-controls"
        aria-hidden
      >
        <div
          className="story-scene-summary text-xs/5 text-taupe-600 dark:text-taupe-500"
          id="story-scene-summary"
        >
          {summaryText}
        </div>
        <button
          id="story-pill"
          type="button"
          data-action={isLastScene ? 'restart' : 'advance'}
          aria-label={isLastScene ? 'Back to top of story' : 'Next section'}
          className={
            'story-pill inline-flex items-center gap-2 rounded-full bg-taupe-950 px-4 py-2 text-sm/6 font-medium text-white shadow-lg hover:bg-taupe-800 dark:bg-taupe-100 dark:text-taupe-950 dark:hover:bg-white' +
            (pillIdle ? ' idle' : '')
          }
          onClick={onPillClick}
        >
          {isLastScene ? '↑ Back to start' : 'Continue ↓'}
        </button>
        <div
          id="story-kbd-hint"
          className="story-kbd-hint hidden font-mono text-[10px] uppercase tracking-widest text-taupe-700 sm:inline dark:text-taupe-300"
          aria-label="Use arrow keys to navigate sections"
          title="Arrow keys: navigate sections"
        >
          ↑↓
        </div>
      </div>

      {/* Side dot navigator — only shown in Story mode. Sandbox has its
          own dot stack (<SandboxSceneDots>) tracking Sandbox sections,
          so we don't want two columns of dots stacked on the left edge
          when the visitor is in Sandbox. */}
      {pilotViewMode === 'story' && (
        <SceneDots
          scenes={sceneDescriptors}
          activeId={activeId}
          onSelect={selectScene}
        />
      )}

      {/* Story-unlock CTA wiring — the inline button lives inside
          StorySections' scene 7. We attach the click handler via a
          one-shot effect to avoid threading an onClick prop through
          the section JSX (it would also have to be wired into the
          legacy-id surface anyway). */}
      <StoryUnlockHandler proc={simState?.proc ?? null} />

      {/* Context-skip CTA wiring — same pattern: button id lives in
          StorySections' scene context; here we attach the handler. */}
      <StoryContextSkipHandler onSkip={() => selectScene('story-scene-2')} />
    </>
  );
}

/* ============================================================
   Side-effect glue — keep imperative paint paths contained here
   so the main component stays declarative.
   ============================================================ */

// paintScene3 was retired by Phase B step 4 of the imperative-
// renderer retirement (docs/eng/pilot-engine-imperative-retirement.md).
// Story scene 3's name / desc / stats / roster all render from
// `simState.proc` directly in the scene 3 JSX above. The `refireScene`
// switch's 'story-scene-3' branch is now a no-op (the React tree
// re-renders automatically when simState changes); kept in the switch
// for shape consistency with scenes 4-7 which still need an
// imperative repaint trigger for their engine-painted SVGs.
function paintScene3(_sim: ProcessSimState) {
  // No-op — see retirement comment above.
}

function paintScene4(sim: ProcessSimState, reduced: boolean) {
  const trace = sampleTrial(sim.proc, sim.runParams, false);
  renderLanes(sim.proc, trace, null, {
    humanSvg: 'story-svg-4-human',
    humanClock: 'story-clock-4',
    totalMs: reduced ? 0 : STORY_TIMINGS.LANE_MS,
  });
  const annot = document.querySelector('#story-annot-4');
  if (annot) {
    annot.textContent = '';
    const delay = reduced ? 0 : STORY_TIMINGS.LANE_MS + STORY_TIMINGS.ANNOT_DELAY_MS;
    window.setTimeout(() => {
      if (annot.isConnected) {
        // Per the May 2026 functional/content review: surface the cost
        // levers (error rates + manual hours) alongside cycle time so
        // the visitor sees what translates to dollars and lost revenue,
        // not just elapsed days. Numbers are bolded so the eye lands
        // on the magnitudes first.
        const days = sim.human.median.toFixed(0);
        const manualHrs = Math.round(sim.human.avgHumanH);
        const failPct = (sim.human.failRate * 100).toFixed(1);
        const b = (txt: string) =>
          `<b class="font-semibold text-taupe-950 dark:text-white">${escapeHtml(txt)}</b>`;
        annot.innerHTML =
          `Around ${b(days + ' days')} on a typical run, ${b(manualHrs + ' hrs')} of manual coordination, ${b(failPct + '%')} of runs die mid-process.`;
      }
    }, delay);
  }
}

function paintScene5(sim: ProcessSimState, reduced: boolean) {
  const traceH = sampleTrial(sim.proc, sim.runParams, false);
  const traceA = sampleTrial(sim.proc, sim.runParams, true);
  renderLanes(sim.proc, traceH, traceA, {
    humanSvg: 'story-svg-5-human',
    humanClock: 'story-clock-5h',
    aiSvg: 'story-svg-5-ai',
    aiClock: 'story-clock-5a',
    totalMs: reduced ? 0 : STORY_TIMINGS.LANE_DUAL_MS,
  });
  const annot = document.querySelector('#story-annot-5');
  if (annot) {
    annot.textContent = '';
    const delay = reduced ? 0 : STORY_TIMINGS.LANE_DUAL_MS + STORY_TIMINGS.ANNOT_DELAY_MS;
    window.setTimeout(() => {
      if (!annot.isConnected) return;
      // Mirrors scene 4's three-axis read so the comparison is
      // apples-to-apples: cycle time, manual hours saved, and failure-
      // rate drop. Numbers are bolded for the same reason as scene 4.
      const h = sim.human.median;
      const a = sim.ai.median;
      const speedup = (h / Math.max(0.01, a)).toFixed(1);
      const manualSaved = Math.round(
        Math.max(0, sim.human.avgHumanH - sim.ai.avgHumanH),
      );
      const failDrop = (
        Math.max(0, sim.human.failRate - sim.ai.failRate) * 100
      ).toFixed(1);
      const b = (txt: string) =>
        `<b class="font-semibold text-taupe-950 dark:text-white">${escapeHtml(txt)}</b>`;
      annot.innerHTML =
        `Around ${b(a.toFixed(0) + ' days')} (${b(speedup + '× faster')}), ${b(manualSaved + ' hrs')} of manual work reclaimed per run, ${b(failDrop + '%')} fewer runs die mid-process.`;
    }, delay);
  }
}

function paintScene6(sim: ProcessSimState, reduced: boolean) {
  renderHistogram(sim.human, sim.ai, {svgId: 'story-svg-histo', branded: true});
  const svg = document.getElementById('story-svg-histo');
  if (svg && !reduced) {
    svg.style.opacity = '0';
    svg.style.transition = `opacity ${STORY_TIMINGS.HISTO_FADE_MS}ms ease-out`;
    requestAnimationFrame(() => {
      svg.style.opacity = '1';
    });
  }
}

function paintScene7(sim: ProcessSimState, reduced: boolean) {
  const hoursSavedPerProc = Math.max(0, sim.human.avgHumanH - sim.ai.avgHumanH);
  const hoursPerYear = hoursSavedPerProc * PROCS_PER_YEAR;
  const dollarsPerYear = hoursPerYear * sim.runParams.hourlyRate;
  const recovered = Math.max(0, sim.human.failRate - sim.ai.failRate) * PROCS_PER_YEAR;

  const dur = reduced ? 0 : STORY_TIMINGS.COUNT_UP_MS;
  animateNumberValue(document.querySelector('#story-win-dollars'), dollarsPerYear, fmtUSD, dur);
  animateNumberValue(
    document.querySelector('#story-win-hours'),
    hoursPerYear,
    (v: number) => `${Math.round(v).toLocaleString()} hrs`,
    dur,
  );
  animateNumberValue(
    document.querySelector('#story-win-deals'),
    recovered,
    (v: number) => (v >= 10 ? `${Math.round(v)}` : v.toFixed(1)),
    dur,
  );
}

function flashPickerNudge() {
  // Phase B step 1 of the imperative-renderer retirement replaced the
  // `#story-cards` div with the React `<ProcessPicker>` component
  // (className still includes `story-cards` for the animation hook).
  // Target the class-name selector now that the id is gone. The
  // title `#story-pick-title` is still id-addressable (it's a
  // <Subheading id="story-pick-title">) so that branch unchanged.
  const cards = document.querySelector('.story-cards');
  if (cards) {
    cards.classList.remove('story-cards-nudge');
    // Force a reflow so the class re-add re-triggers the CSS animation.
    void (cards as HTMLElement).offsetWidth;
    cards.classList.add('story-cards-nudge');
  }
  const title = document.querySelector('#story-pick-title');
  if (title) {
    title.classList.remove('story-cards-nudge');
    void (title as HTMLElement).offsetWidth;
    title.classList.add('story-cards-nudge');
  }
}

function dispatchUnlock(proc: ProcessDef | null) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MP_UNLOCK_EVENT, {detail: {proc}}));
}

/* ============================================================
   StoryUnlockHandler — one-shot effect that binds the in-DOM
   #story-unlock button (rendered inside StorySections scene 7)
   to dispatch mp:unlock. Kept as a tiny child so the binding
   happens after the StorySections subtree has mounted.
   ============================================================ */
function StoryUnlockHandler({proc}: {proc: ProcessDef | null}) {
  const procRef = useRef(proc);
  procRef.current = proc;

  useEffect(() => {
    const btn = document.querySelector('#story-unlock');
    if (!btn) return;
    const handler = (e: Event) => {
      e.preventDefault();
      dispatchUnlock(procRef.current);
    };
    btn.addEventListener('click', handler);
    return () => btn.removeEventListener('click', handler);
  }, []);
  return null;
}

function StoryContextSkipHandler({onSkip}: {onSkip: () => void}) {
  const onSkipRef = useRef(onSkip);
  onSkipRef.current = onSkip;
  useEffect(() => {
    const btn = document.querySelector('#story-context-skip');
    if (!btn) return;
    const handler = (e: Event) => {
      e.preventDefault();
      onSkipRef.current();
    };
    btn.addEventListener('click', handler);
    return () => btn.removeEventListener('click', handler);
  }, []);
  return null;
}
